import { Box } from "./box";
import { Images } from "./images";
import { Encoder } from "./encoder";
import { CornerType } from "./model/corner-type";
import { ColorType2 } from "./model/color-type2";

export interface KonvaBox {
  readonly attrs: { [id: string]: any };
  setAttr(attrName: string, attrVal: any): void;
  setWidth(size: number): void;
  setHeight(size: number): void;
  setFillPatternScaleX(size: number): void;
  setFillPatternScaleY(size: number): void;
  destroy(): void;
}

export interface CreateBoxCmd {
  readonly size:
    | "small"
    | "tall"
    | "wide"
    | "large"
    | "small-corner"
    | "tall-corner";

  readonly x: number;

  readonly y: number;

  readonly scaleX: number;

  readonly scaleY: number;

  readonly width: number;

  readonly height: number;

  readonly back: "0" | "n" | "w" | undefined;

  readonly door: "0" | "n" | "w" | undefined;

  readonly drawers: "000" | "n" | "w" | undefined;

  readonly shelf: "0000" | "n" | "w" | undefined;

  readonly direction: "r" | "l" | undefined;

  readonly crossDivider: "0" | "n" | "w" | undefined;
}

export type ColorType = "n" | "w";

export type ViewType = "iso" | "2d";

export interface CartItemContent {
  readonly variantId: number;

  readonly pricePerUnit: number;

  count: number;
}

export interface CartContent {
  readonly boxes: {
    readonly small: CartItemContent;
    readonly tall: CartItemContent;
    readonly wide: CartItemContent;
    readonly large: CartItemContent;
  };
  readonly back: {
    readonly small: CartItemContent;
    readonly tallWide: CartItemContent;
    readonly large: CartItemContent;
  };
  readonly door: {
    readonly small: CartItemContent;
    readonly tallWide: CartItemContent;
    readonly large: CartItemContent;
  };
  readonly shelf: {
    readonly smallTall: CartItemContent;
    readonly wideLarge: CartItemContent;
  };
  readonly drawer: {
    readonly smallTall: CartItemContent;
    readonly wideLarge: CartItemContent;
  };
  readonly divider: {
    readonly small: CartItemContent;
    readonly tallWide: CartItemContent;
    readonly large: CartItemContent;
  };
  readonly corner: {
    readonly small: CartItemContent;
    readonly tall: CartItemContent;
  };
}

export class BoxService {
  private _color: ColorType2 = ColorType2.walnut;
  private readonly boxes: { konva: KonvaBox; domain: Box }[] = [];

  private readonly _images: Images;

  private readonly _encoder: Encoder;

  constructor(images: Images, encoder: Encoder) {
    this._images = images;
    this._encoder = encoder;
  }

  public createBox(
    config: CreateBoxCmd,
    konvaBoxFactory: (config) => KonvaBox,
  ): KonvaBox | null {
    if (config.size === "tall" && !this.isTallBoxAvailable()) {
      return null;
    }

    if (config.size === "tall-corner") {
      if (!this.isCornerAvailable(CornerType.tallCorner)) {
        return null;
      }
    }

    const box = new Box(this.boxes.length + 1, <ColorType>this._color.code);
    box.setSize(config.size);

    if (config.back === "n" || config.back === "w") {
      box.addBack();
    }

    if (config.door === "n" || config.door === "w") {
      if (this.supportsDoor2(box)) {
        box.addDoor();
      }
    }

    if (config.shelf) {
      this._encoder.decodeShelf(config.shelf, box);
    }

    if (config.drawers) {
      this._encoder.decodeDrawers(config.drawers, box);
    }

    if (config.direction) {
      this._encoder.decodeCornerDirection(config.direction, box);
    }

    if (config.crossDivider) {
      this._encoder.decodeCrossDivider(config.crossDivider, box);
    }

    const konvaBoxProps = {
      x: config.x ?? 80,
      y: config.y ?? 80,
      // fill: '#432616',
      // stroke: 'red',
      // strokeWidth: 0,
      fillAfterStrokeEnabled: true,
      draggable: true,
      fillPatternScaleX: config.scaleX,
      fillPatternScaleY: config.scaleY,
      fillPatternRepeat: "no-repeat",
      width: config.width,
      height: config.height,
    };
    const konvaBox = konvaBoxFactory(konvaBoxProps);

    const assoc = {
      konva: konvaBox,
      domain: box,
    };

    this.boxes.push(assoc);

    this.ensureCompat(assoc);

    return konvaBox;
  }

  public deleteBox(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    const index = this.boxes.indexOf(assocMaybe);

    if (index >= 0) {
      this.boxes.splice(index, 1);

      assocMaybe.konva.destroy();
    }
  }

  public isCorner(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const isCorner = assocMaybe.domain.isCorner();

    return isCorner;
  }

  public hasBack(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const has = assocMaybe.domain.hasBack();

    return has;
  }

  public supportsDoor(konvaBox: KonvaBox) {
    const box = this.getDomainBoxMaybe(konvaBox);

    if (!box) {
      return false;
    }

    return this.supportsDoor2(box);
  }

  private supportsDoor2(_box: Box) {
    return true;
  }

  public hasDoors(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const has = assocMaybe.domain.hasDoor();

    return has;
  }

  public hasShelves(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const has = assocMaybe.domain.hasShelves();

    return has;
  }

  public hasTopDrawer(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const has = assocMaybe.domain.hasDrawerTop();

    return has;
  }

  public hasMiddleDrawer(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const has = assocMaybe.domain.hasDrawerMiddle();

    return has;
  }

  public hasBottomDrawer(konvaBox: KonvaBox): boolean {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    const has = assocMaybe.domain.hasDrawerBottom();

    return has;
  }

  public supportsBack(konvaBox: KonvaBox): boolean | undefined {
    return this.getDomainBoxMaybe(konvaBox).supportsBack();
  }

  public getColor(): ColorType {
    return this._color.code as ColorType;
  }

  public getColor2(): ColorType2 {
    return this._color;
  }

  public setColor(color: ColorType) {
    if (color === "n") {
      this._color = ColorType2.natural;
    }

    if (color === "w") {
      this._color = ColorType2.walnut;
    }
  }

  public getShelvesCount(konvaBox: KonvaBox): number | undefined {
    const box = this.getDomainBoxMaybe(konvaBox);

    const count = box?.getShelfCount();

    return count;
  }

  public toggleColor() {
    if (this._color.is(ColorType2.natural)) {
      this._color = ColorType2.walnut;
    } else {
      this._color = ColorType2.natural;
    }

    for (const assoc of [...this.boxes]) {
      if (!assoc.domain.canChangeColor(this._color)) {
        this.deleteBox(assoc.konva);
      } else {
        assoc.domain.changeColor(this._color.code as ColorType);

        if (assoc.domain.hasBack() && !this.supportsBack(assoc.konva)) {
          assoc.domain.removeBack();
        }

        if (assoc.domain.hasDoor() && !this.supportsDoor(assoc.konva)) {
          assoc.domain.removeDoor();
        }

        this.ensureCompat(assoc);
      }
    }
  }

  public toggleBack(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    if (!this.supportsBack(assocMaybe.konva)) {
      if (assocMaybe.domain.hasBack()) {
        assocMaybe.domain.removeBack();
      }

      return;
    }

    assocMaybe.domain.toggleBack();

    this.ensureCompat(assocMaybe);
  }

  public toggleDoor(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    if (!this.supportsDoor(assocMaybe.konva)) {
      if (assocMaybe.domain.hasDoor()) {
        assocMaybe.domain.removeDoor();
      }

      return;
    }

    assocMaybe.domain.toggleDoor();

    this.ensureCompat(assocMaybe);
  }

  public toggleShelf(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    assocMaybe.domain.toggleShelf();

    this.ensureCompat(assocMaybe);
  }

  public toggleDrawerTop(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    assocMaybe.domain.toggleDrawerTop();

    this.ensureCompat(assocMaybe);
  }

  public toggleDrawerMiddle(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    assocMaybe.domain.toggleDrawerMiddle();

    this.ensureCompat(assocMaybe);
  }

  public toggleDrawerBottom(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    assocMaybe.domain.toggleDrawerBottom();

    this.ensureCompat(assocMaybe);
  }

  public toggleDirection(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    assocMaybe.domain.toggleDirection();

    this.ensureCompat(assocMaybe);
  }

  public supportsCrossDivider(konvaBox: KonvaBox): boolean | undefined {
    return this.getDomainBoxMaybe(konvaBox)?.supportsCrossDivider();
  }

  public hasCrossDivider(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return false;
    }

    return assocMaybe.domain.hasCrossDivider();
  }

  public toggleCrossDivider(konvaBox: KonvaBox) {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return;
    }

    assocMaybe.domain.toggleCrossDivider();

    this.ensureCompat(assocMaybe);
  }

  public getKonvoBoxes(): KonvaBox[] {
    return this.boxes.map((b) => b.konva);
  }

  public getKonvaBox(box: Box): KonvaBox | undefined {
    const assoc = this.boxes.filter((b) => b.domain === box)[0];

    return assoc?.konva;
  }

  public getBoxes(): Box[] {
    return this.boxes.map((b) => b.domain);
  }

  public getImage(
    view: ViewType,
    konvaCube: KonvaBox,
  ): HTMLImageElement | undefined {
    const assocMaybe = this.getAssocByKonvaBox(konvaCube);

    if (!assocMaybe) {
      return;
    }

    const box = assocMaybe.domain;

    const imageName = this._encoder.encodeImageName(box);

    const imageMaybe = this._images.getImage(view, imageName);

    return imageMaybe;
  }

  public getComponentsCount() {
    const finalCount = {
      small: {
        box: {
          count: 0,
          variantIdByFinish: {
            natural: 40325915279553,
            darkWalnut: 40325915246785,
          },
        },
        back: {
          count: 0,
          variantIdByFinish: {
            natural: 40325980094657,
            darkWalnut: 40325980061889,
          },
        },
        door: {
          count: 0,
          variantIdByFinish: {
            natural: 40326000476353,
            darkWalnut: 40326000410817,
          },
        },
        shelf: {
          count: 0,
          variantIdByFinish: {
            natural: 40326010175681,
            darkWalnut: 40326010142913,
          },
        },
        drawer: {
          count: 0,
          variantIdByFinish: {
            natural: 40326038782145,
            darkWalnut: 40326038749377,
          },
        },
        right: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        left: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        divider: {
          count: 0,
          variantIdByFinish: {
            natural: 42801173266625,
            darkWalnut: 42801173233857,
          },
        },
      },
      tall: {
        box: {
          count: 0,
          variantIdByFinish: {
            natural: 40325915345089,
            darkWalnut: 40325915312321,
          },
        },
        back: {
          count: 0,
          variantIdByFinish: {
            natural: 40325980160193,
            darkWalnut: 40325980127425,
          },
        },
        door: {
          count: 0,
          variantIdByFinish: {
            natural: 40326000541889,
            darkWalnut: 40326000509121,
          },
        },
        shelf: {
          count: 0,
          variantIdByFinish: {
            natural: 40326010175681,
            darkWalnut: 40326010142913,
          },
        },
        drawer: {
          count: 0,
          variantIdByFinish: {
            natural: 40326038782145,
            darkWalnut: 40326038749377,
          },
        },
        right: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        left: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        divider: {
          count: 0,
          variantIdByFinish: {
            natural: 42801173332161,
            darkWalnut: 42801173299393,
          },
        },
      },
      wide: {
        box: {
          count: 0,
          variantIdByFinish: {
            natural: 40325915410625,
            darkWalnut: 40325915377857,
          },
        },
        back: {
          count: 0,
          variantIdByFinish: {
            natural: 40325980160193,
            darkWalnut: 40325980127425,
          },
        },
        door: {
          count: 0,
          variantIdByFinish: {
            natural: 40326000541889,
            darkWalnut: 40326000509121,
          },
        },
        shelf: {
          count: 0,
          variantIdByFinish: {
            natural: 40326010241217,
            darkWalnut: 40326010208449,
          },
        },
        drawer: {
          count: 0,
          variantIdByFinish: {
            natural: 40326038847681,
            darkWalnut: 40326038814913,
          },
        },
        right: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        left: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        divider: {
          count: 0,
          variantIdByFinish: {
            natural: 42801173332161,
            darkWalnut: 42801173299393,
          },
        },
      },
      large: {
        box: {
          count: 0,
          variantIdByFinish: {
            natural: 40325915476161,
            darkWalnut: 40325915443393,
          },
        },
        back: {
          count: 0,
          variantIdByFinish: {
            natural: 40325980258497,
            darkWalnut: 40325980192961,
          },
        },
        door: {
          count: 0,
          variantIdByFinish: {
            natural: 40326000607425,
            darkWalnut: 40326000574657,
          },
        },
        shelf: {
          count: 0,
          variantIdByFinish: {
            natural: 40326010241217,
            darkWalnut: 40326010208449,
          },
        },
        drawer: {
          count: 0,
          variantIdByFinish: {
            natural: 40326038847681,
            darkWalnut: 40326038814913,
          },
        },
        right: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        left: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        divider: {
          count: 0,
          variantIdByFinish: {
            natural: 42801173397697,
            darkWalnut: 42801173364929,
          },
        },
      },
      "small-corner": {
        box: {
          count: 0,
          variantIdByFinish: {
            natural: 42801168941249,
            darkWalnut: 42801168908481,
          },
        },
        back: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        door: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        shelf: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        drawer: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        right: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        left: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        divider: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
      },
      "tall-corner": {
        box: {
          count: 0,
          variantIdByFinish: {
            natural: 42801169006785,
            darkWalnut: 42801168974017,
          },
        },
        back: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        door: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        shelf: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        drawer: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        right: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        left: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
        divider: {
          count: 0,
          variantIdByFinish: {
            natural: 0,
            darkWalnut: 0,
          },
        },
      },
    };

    for (const { domain: box } of this.boxes) {
      const size = box.getSize();

      if (box.isCorner()) {
        if (box.isDirectionRight()) {
          finalCount[size].right.count++;
        }
        if (box.isDirectionLeft()) {
          finalCount[size].left.count++;
        }
      } else {
        finalCount[size].box.count++;

        if (box.hasBack()) {
          finalCount[size].back.count++;
        }

        if (box.hasDoor()) {
          finalCount[size].door.count++;
        }

        if (box.hasShelves()) {
          const shelves = box.getShelfCount();

          finalCount[size].shelf.count += shelves;
        }

        if (box.hasCrossDivider()) {
          finalCount[size].divider.count++;
        }

        if (
          box.hasDrawerTop() ||
          box.hasDrawerMiddle() ||
          box.hasDrawerBottom()
        ) {
          const drawers = box.getDrawersCount();

          finalCount[size].drawer.count += drawers;
        }
      }
    }

    return finalCount;
  }

  public getCartCount(): CartItemContent[] {
    const variantIds = {
      box: {
        small: {
          n: 40325915279553,
          w: 40325915246785,
        },
        tall: {
          n: 40325915345089,
          w: 40325915312321,
        },
        wide: {
          n: 40325915410625,
          w: 40325915377857,
        },
        large: {
          n: 40325915476161,
          w: 40325915443393,
        },
      },
      back: {
        small: {
          n: 40325980094657,
          w: 40325980061889,
        },
        tallWide: {
          n: 40325980160193,
          w: 40325980127425,
        },
        large: {
          n: 40325980258497,
          w: 40325980192961,
        },
      },
      door: {
        small: {
          n: 40326000476353,
          w: 40326000410817,
        },
        tallWide: {
          n: 40326000541889,
          w: 40326000509121,
        },
        large: {
          n: 40326000607425,
          w: 40326000574657,
        },
      },
      shelf: {
        smallTall: {
          n: 40326010175681,
          w: 40326010142913,
        },
        wideLarge: {
          n: 40326010241217,
          w: 40326010208449,
        },
      },
      drawer: {
        smallTall: {
          n: 40326038782145,
          w: 40326038749377,
        },
        wideLarge: {
          n: 40326038847681,
          w: 40326038814913,
        },
      },
      divider: {
        small: {
          n: 42801173266625,
          w: 42801173233857,
        },
        tallWide: {
          n: 42801173332161,
          w: 42801173299393,
        },
        large: {
          n: 42801173397697,
          w: 42801173364929,
        },
      },
      corner: {
        small: {
          n: 42801168941249,
          w: 42801168908481,
        },
        tall: {
          n: 42801169006785,
          w: 42801168974017,
        },
      },
    };
    const prices = {
      box: {
        small: 69,
        tallWide: 84,
        large: 99,
      },
      back: {
        small: 14,
        tallWide: 19,
        large: 24,
      },
      door: {
        small: 24,
        tallWide: 29,
        large: 34,
      },
      shelf: {
        smallTall: 14,
        wideLarge: 19,
      },
      drawer: {
        smallTall: 39,
        wideLarge: 59,
      },
      divider: {
        small: 29,
        tallWide: 34,
        large: 39,
      },
      corner: {
        small: 69,
        tall: 84,
      },
    };
    const cart: CartContent = {
      boxes: {
        small: {
          count: 0,
          pricePerUnit: prices.box.small,
          variantId: variantIds.box.small[this._color.code],
        },
        tall: {
          count: 0,
          pricePerUnit: prices.box.tallWide,
          variantId: variantIds.box.tall[this._color.code],
        },
        wide: {
          count: 0,
          pricePerUnit: prices.box.tallWide,
          variantId: variantIds.box.wide[this._color.code],
        },
        large: {
          count: 0,
          pricePerUnit: prices.box.large,
          variantId: variantIds.box.large[this._color.code],
        },
      },
      back: {
        small: {
          count: 0,
          pricePerUnit: prices.back.small,
          variantId: variantIds.back.small[this._color.code],
        },
        tallWide: {
          count: 0,
          pricePerUnit: prices.back.tallWide,
          variantId: variantIds.back.tallWide[this._color.code],
        },
        large: {
          count: 0,
          pricePerUnit: prices.back.large,
          variantId: variantIds.back.large[this._color.code],
        },
      },
      door: {
        small: {
          count: 0,
          pricePerUnit: prices.door.small,
          variantId: variantIds.door.small[this._color.code],
        },
        tallWide: {
          count: 0,
          pricePerUnit: prices.door.tallWide,
          variantId: variantIds.door.tallWide[this._color.code],
        },
        large: {
          count: 0,
          pricePerUnit: prices.door.large,
          variantId: variantIds.door.large[this._color.code],
        },
      },
      shelf: {
        smallTall: {
          count: 0,
          pricePerUnit: prices.shelf.smallTall,
          variantId: variantIds.shelf.smallTall[this._color.code],
        },
        wideLarge: {
          count: 0,
          pricePerUnit: prices.shelf.wideLarge,
          variantId: variantIds.shelf.wideLarge[this._color.code],
        },
      },
      drawer: {
        smallTall: {
          count: 0,
          pricePerUnit: prices.drawer.smallTall,
          variantId: variantIds.drawer.smallTall[this._color.code],
        },
        wideLarge: {
          count: 0,
          pricePerUnit: prices.drawer.wideLarge,
          variantId: variantIds.drawer.wideLarge[this._color.code],
        },
      },
      divider: {
        small: {
          count: 0,
          pricePerUnit: prices.divider.small,
          variantId: variantIds.divider.small[this._color.code],
        },
        tallWide: {
          count: 0,
          pricePerUnit: prices.divider.tallWide,
          variantId: variantIds.divider.tallWide[this._color.code],
        },
        large: {
          count: 0,
          pricePerUnit: prices.divider.large,
          variantId: variantIds.divider.large[this._color.code],
        },
      },
      corner: {
        small: {
          count: 0,
          pricePerUnit: prices.corner.small,
          variantId: variantIds.corner.small[this._color.code],
        },
        tall: {
          count: 0,
          pricePerUnit: prices.corner.tall,
          variantId: variantIds.corner.tall[this._color.code],
        },
      },
    };

    for (const { domain: box } of this.boxes) {
      const size = box.getSize();

      if (box.isCorner()) {
        switch (size) {
          case "small-corner":
            cart.corner.small.count++;
            break;
          case "tall-corner":
            cart.corner.tall.count++;
            break;
        }
      } else {
        switch (size) {
          case "small":
            cart.boxes.small.count++;
            break;
          case "tall":
            cart.boxes.tall.count++;
            break;
          case "wide":
            cart.boxes.wide.count++;
            break;
          case "large":
            cart.boxes.large.count++;
            break;
        }

        if (box.hasBack()) {
          switch (size) {
            case "small":
              cart.back.small.count++;
              break;
            case "tall":
            case "wide":
              cart.back.tallWide.count++;
              break;
            case "large":
              cart.back.large.count++;
              break;
          }
        }

        if (box.hasDoor()) {
          switch (size) {
            case "small":
              cart.door.small.count++;
              break;
            case "tall":
            case "wide":
              cart.door.tallWide.count++;
              break;
            case "large":
              cart.door.large.count++;
              break;
          }
        }

        if (box.hasShelves()) {
          const shelves = box.getShelfCount();

          switch (size) {
            case "small":
            case "tall":
              cart.shelf.smallTall.count += shelves;
              break;
            case "wide":
            case "large":
              cart.shelf.wideLarge.count += shelves;
              break;
          }
        }

        if (
          box.hasDrawerTop() ||
          box.hasDrawerMiddle() ||
          box.hasDrawerBottom()
        ) {
          const drawers = box.getDrawersCount();

          switch (size) {
            case "small":
            case "tall":
              cart.drawer.smallTall.count += drawers;
              break;
            case "wide":
            case "large":
              cart.drawer.wideLarge.count += drawers;
              break;
          }
        }

        if (box.hasCrossDivider()) {
          switch (size) {
            case "small":
              cart.divider.small.count++;
              break;
            case "tall":
            case "wide":
              cart.divider.tallWide.count++;
              break;
            case "large":
              cart.divider.large.count++;
              break;
          }
        }
      }
    }

    if (cart.door.small.count % 2 === 1) {
      cart.door.small.count++;
    }
    if (cart.door.tallWide.count % 2 === 1) {
      cart.door.tallWide.count++;
    }
    if (cart.door.large.count % 2 === 1) {
      cart.door.large.count++;
    }

    if (cart.shelf.smallTall.count % 2 === 1) {
      cart.shelf.smallTall.count++;
    }
    if (cart.shelf.wideLarge.count % 2 === 1) {
      cart.shelf.wideLarge.count++;
    }

    if (cart.drawer.smallTall.count % 2 === 1) {
      cart.drawer.smallTall.count++;
    }
    if (cart.drawer.wideLarge.count % 2 === 1) {
      cart.drawer.wideLarge.count++;
    }

    const cartItems = [
      cart.boxes.small,
      cart.boxes.tall,
      cart.boxes.wide,
      cart.boxes.large,

      cart.back.small,
      cart.back.tallWide,
      cart.back.large,

      cart.door.small,
      cart.door.tallWide,
      cart.door.large,

      cart.shelf.smallTall,
      cart.shelf.wideLarge,

      cart.drawer.smallTall,
      cart.drawer.wideLarge,

      cart.divider.small,
      cart.divider.tallWide,
      cart.divider.large,

      cart.corner.small,
      cart.corner.tall,
    ].filter((c) => c.count > 0);

    return cartItems;
  }

  public getCartCost(): number {
    const finalCart = this.getCartCount();

    const total = finalCart.reduce(
      (agg, curr) => agg + curr.count * curr.pricePerUnit,
      0,
    );

    return total;
  }

  private getAssocByKonvaBox(
    konvaBox: KonvaBox,
  ): { konva: KonvaBox; domain: Box } | undefined {
    const assocMaybe = this.boxes.filter((b) => b.konva === konvaBox)[0];

    return assocMaybe;
  }

  private getDomainBoxMaybe(konvaBox: KonvaBox): Box | undefined {
    const assocMaybe = this.getAssocByKonvaBox(konvaBox);

    if (!assocMaybe) {
      return undefined;
    }

    return assocMaybe.domain;
  }

  private ensureCompat(assoc: { konva: KonvaBox; domain: Box }) {
    const box = assoc.domain;
    const konvaBox = assoc.konva;

    konvaBox.setAttr("cube_color", this._color);
    konvaBox.setAttr("cube_size", box.getSize());
    konvaBox.setAttr("box_index", box.getIndex());
    konvaBox.setAttr("back", box.hasBack());
    konvaBox.setAttr("door", box.hasDoor());
    konvaBox.setAttr("top_drawer", box.hasDrawerTop());
    konvaBox.setAttr("middle_drawer", box.hasDrawerMiddle());
    konvaBox.setAttr("bottom_drawer", box.hasDrawerBottom());
    konvaBox.setAttr("shelf", box.getShelfCount());
    konvaBox.setAttr("corner_right", box.isDirectionRight());
    konvaBox.setAttr("cross_divider", box.hasCrossDivider());
  }

  public isCornerAvailable(type: CornerType) {
    return true;
  }

  public isTallBoxAvailable() {
    return !this._color.is(ColorType2.walnut);
  }
}
